import { graphql, Link } from 'gatsby';
import React, { useState } from 'react';
import { Column, Container, Row } from '../../components/Grid';
import Layout from '../../components/Layout';
import Section from '../../components/Section';
import Video from '../../components/Video';
import { getKeyedResources } from '../../utils/resource-helper';
import Resource from '../../components/Resource';
import Image from '../../components/Image';

const ClassroomResourcesPage = ({ data }) => {
  const {
    phonemicAwarenessRythm,
    phonemicAwarenessSoundsSequence,
    phonemicAwarenessSoundManipulation,
    phonemicAwarenessMasterclass,
    taskCards,
    phonemicAwarenessRtuRythm,
    phonemicAwarenessRtuSoundsSequence,
    phonemicAwarenessRtuSoundManipulation,
    phonemicAwarenessRtuAlliteration,
    phonemicAwarenessRtuSyllabication,
  } = getKeyedResources(data);
  const [isTabExpanded, setIsTabExpanded] = useState(false);

  return (
    <Layout
      title="Phonological Awareness"
      className="classroom-resources-subpage classroom-resources-subpage--phonological-awareness"
    >
      <Section className="classroom-resources-subpage-header-section">
        <Container>
          <div className="classroom-resources-subpage-header classroom-resources-subpage-header--phonological-awareness">
            <div className="classroom-resources-subpage-header-top-section">
              <Link
                to="/classroom-resources"
                className="classroom-resources-subpage-header-back-button"
              >
                <Image
                  className="classroom-resources-subpage-header-back-icon"
                  filename="back-button-icon.png"
                  alt="Back"
                />
                <span>Back to Learning Resources Overview</span>
              </Link>
              <div className="classroom-resources-subpage-header-resources-tab">
                <button
                  className={`classroom-resources-subpage-header-tab-button ${
                    isTabExpanded ? 'expanded' : ''
                  }`}
                  onClick={() => setIsTabExpanded(!isTabExpanded)}
                >
                  How to use these resources
                  <span className="caret-icon">▼</span>
                </button>
                {isTabExpanded && (
                  <div className="classroom-resources-subpage-header-tab-content">
                    <p>
                      <b>1. Start with the Explainer Video:</b> Watch the
                      Explainer video to understand the key concepts of each big
                      idea of literacy.
                      <br />
                      <b>2. Dive into the Microlearns:</b> Complete the 6–8
                      minute, self-paced modules for each big idea. These
                      modules provide research-based strategies and activities
                      you can immediately use with your students.
                      <br />
                      <b>3. See the Strategies in Action:</b> Watch the masterclass
                      video, where a teacher demonstrates these strategies in a
                      real-world classroom setting.
                      <br />
                      <b>4. Equip Yourself with Task Cards:</b> Print the task
                      cards to keep these strategies at your fingertips and use
                      the printable manipulatives to support hands-on learning
                      with students.
                      <br />
                      <b>5. Apply What You’ve Learned:</b> Use the ready-to-use
                      activity to teach foundational literacy skills like
                      phonics, vocabulary, and comprehension directly in your
                      classroom or after-school program.
                      <br />
                      <b>Why This Approach Works:</b> Each step builds on the
                      previous one, helping you gain foundational knowledge, see
                      effective strategies in practice, and implement them
                      confidently. Whether you’re a classroom teacher or
                      afterschool professional, these resources provide the tools
                      you need to create engaging, impactful literacy experiences
                      for K–2 learners.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <h1 className="classroom-resources-subpage-header-title">
              Phonological Awareness
            </h1>
            <p className="classroom-resources-subpage-header-description">
              Explore the power that students have to engage in meaningful
              interactions by learning to identify and manipulate the individual
              sound units within spoken words.
            </p>
          </div>
        </Container>
      </Section>
      <Section style={{ padding: '0' }}>
        <Container>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button classroom-resources-subpage-header-back-button--mobile"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className="classroom-resources-subpage-video">
            <Video
              guidSrc="26bd40e2-1ed7-4fd4-a75a-7267ba71b92d"
              title="What is Phonological Awareness?"
            />
            <p className="classroom-resources-subpage-video-category">
              Explainer video
            </p>
            <p className="classroom-resources-subpage-video-title">
              What is Phonological Awareness?
            </p>
            <p className="classroom-resources-subpage-video-description">
              Explore how sounds are used to form the building blocks of words
              by learning about phonological awareness.
            </p>
          </div>
        </Container>
      </Section>
      <Section className="classroom-resources-subpage-resources classroom-resources-subpage-resources--phonological-awareness">
        <Container>
          <h2 className="classroom-resources-subpage-resources-title">
            Supporting Resources
          </h2>
          <Row>
            <Column size={4}>
              <Resource
                tophat={phonemicAwarenessRythm.tophat}
                tophatColor={phonemicAwarenessRythm.tophatColor}
                title={phonemicAwarenessRythm.title}
                duration={phonemicAwarenessRythm.duration}
                className={phonemicAwarenessRythm.className}
                subtitle={phonemicAwarenessRythm.subtitle}
                description={phonemicAwarenessRythm.description}
                img={phonemicAwarenessRythm.img}
                actions={phonemicAwarenessRythm.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonemicAwarenessSoundsSequence.tophat}
                tophatColor={phonemicAwarenessSoundsSequence.tophatColor}
                title={phonemicAwarenessSoundsSequence.title}
                duration={phonemicAwarenessSoundsSequence.duration}
                className={phonemicAwarenessSoundsSequence.className}
                subtitle={phonemicAwarenessSoundsSequence.subtitle}
                description={phonemicAwarenessSoundsSequence.description}
                img={phonemicAwarenessSoundsSequence.img}
                actions={phonemicAwarenessSoundsSequence.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonemicAwarenessSoundManipulation.tophat}
                tophatColor={phonemicAwarenessSoundManipulation.tophatColor}
                title={phonemicAwarenessSoundManipulation.title}
                duration={phonemicAwarenessSoundManipulation.duration}
                className={phonemicAwarenessSoundManipulation.className}
                subtitle={phonemicAwarenessSoundManipulation.subtitle}
                description={phonemicAwarenessSoundManipulation.description}
                img={phonemicAwarenessSoundManipulation.img}
                actions={phonemicAwarenessSoundManipulation.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card"
              size={12}
            >
              <Resource
                horizontal
                tophat={phonemicAwarenessMasterclass.tophat}
                tophatColor={phonemicAwarenessMasterclass.tophatColor}
                title={phonemicAwarenessMasterclass.title}
                duration={phonemicAwarenessMasterclass.duration}
                className={phonemicAwarenessMasterclass.className}
                subtitle={phonemicAwarenessMasterclass.subtitle}
                description={phonemicAwarenessMasterclass.description}
                img={phonemicAwarenessMasterclass.img}
                actions={phonemicAwarenessMasterclass.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card"
              size={12}
            >
              <Resource
                horizontal
                tophat={taskCards.tophat}
                tophatColor={taskCards.tophatColor}
                title={taskCards.title}
                duration={taskCards.duration}
                className={taskCards.className}
                subtitle={taskCards.subtitle}
                description={taskCards.description}
                img={taskCards.img}
                actions={taskCards.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column size={4}>
              <Resource
                tophat={phonemicAwarenessRtuSyllabication.tophat}
                tophatColor={phonemicAwarenessRtuSyllabication.tophatColor}
                title={phonemicAwarenessRtuSyllabication.title}
                duration={phonemicAwarenessRtuSyllabication.duration}
                className={phonemicAwarenessRtuSyllabication.className}
                subtitle={phonemicAwarenessRtuSyllabication.subtitle}
                description={phonemicAwarenessRtuSyllabication.description}
                img={phonemicAwarenessRtuSyllabication.img}
                actions={phonemicAwarenessRtuSyllabication.actions}
              />
            </Column>
          <Column size={4}>
              <Resource
                tophat={phonemicAwarenessRtuAlliteration.tophat}
                tophatColor={phonemicAwarenessRtuAlliteration.tophatColor}
                title={phonemicAwarenessRtuAlliteration.title}
                duration={phonemicAwarenessRtuAlliteration.duration}
                className={phonemicAwarenessRtuAlliteration.className}
                subtitle={phonemicAwarenessRtuAlliteration.subtitle}
                description={phonemicAwarenessRtuAlliteration.description}
                img={phonemicAwarenessRtuAlliteration.img}
                actions={phonemicAwarenessRtuAlliteration.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonemicAwarenessRtuRythm.tophat}
                tophatColor={phonemicAwarenessRtuRythm.tophatColor}
                title={phonemicAwarenessRtuRythm.title}
                duration={phonemicAwarenessRtuRythm.duration}
                className={phonemicAwarenessRtuRythm.className}
                subtitle={phonemicAwarenessRtuRythm.subtitle}
                description={phonemicAwarenessRtuRythm.description}
                img={phonemicAwarenessRtuRythm.img}
                actions={phonemicAwarenessRtuRythm.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonemicAwarenessRtuSoundsSequence.tophat}
                tophatColor={phonemicAwarenessRtuSoundsSequence.tophatColor}
                title={phonemicAwarenessRtuSoundsSequence.title}
                duration={phonemicAwarenessRtuSoundsSequence.duration}
                className={phonemicAwarenessRtuSoundsSequence.className}
                subtitle={phonemicAwarenessRtuSoundsSequence.subtitle}
                description={phonemicAwarenessRtuSoundsSequence.description}
                img={phonemicAwarenessRtuSoundsSequence.img}
                actions={phonemicAwarenessRtuSoundsSequence.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonemicAwarenessRtuSoundManipulation.tophat}
                tophatColor={phonemicAwarenessRtuSoundManipulation.tophatColor}
                title={phonemicAwarenessRtuSoundManipulation.title}
                duration={phonemicAwarenessRtuSoundManipulation.duration}
                className={phonemicAwarenessRtuSoundManipulation.className}
                subtitle={phonemicAwarenessRtuSoundManipulation.subtitle}
                description={phonemicAwarenessRtuSoundManipulation.description}
                img={phonemicAwarenessRtuSoundManipulation.img}
                actions={phonemicAwarenessRtuSoundManipulation.actions}
              />
            </Column>
          </Row>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
    </Layout>
  );
};

export const query = graphql`
  query PhonoAwarenessResources {
    allResourcesJson(filter: { pages: { in: "classroom-resources" } }) {
      edges {
        node {
          slug
          tophat
          tophatColor
          img
          icon
          subject
          title
          subtitle
          duration
          description
          actions {
            label
            metadata
            module
            moduleName
            link
            download
            noIcon
            asLink
            videoGuid
          }
          horizontal
          className
          pages
        }
      }
    }
  }
`;

export default ClassroomResourcesPage;
